// src/pages/comercial/components/CriarObjetivosAccClientes.js
import React, { useMemo, useRef, useEffect } from "react";

import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

const ACC_SX = {
  backgroundColor: "#fff7f0",
  borderRadius: 2,
  "&:before": { display: "none" },
};

const ACC_SUM_SX = {
  minHeight: 32,
  px: 1.5,
  "& .MuiAccordionSummary-content": { margin: "4px 0" },
};

export default function CriarObjetivosAccClientes({
  panelKey = "clientes",
  expandedPanel,
  onChange,
  disabled = false,
  hint = null,

  clientesOptions, // [{value,label}]
  clientesModoTodos,
  setClientesModoTodos,
  clientesSel,
  setClientesSel,
}) {
  const all = useMemo(() => (clientesOptions || []).map((x) => x.value), [clientesOptions]);

  // "todos selecionados" no modo manual
  const isAll = useMemo(
    () => !clientesModoTodos && (clientesSel?.length || 0) > 0 && clientesSel.length === all.length,
    [clientesModoTodos, clientesSel, all]
  );

  // =========================================================
  // Ao FECHAR o accordion:
  // - Se estiver em modo manual e sem clientes selecionados,
  //   volta automaticamente para "Todos incluídos"
  // =========================================================
  const wasExpandedRef = useRef(false);
  const isExpanded = expandedPanel === panelKey;

  useEffect(() => {
    const wasExpanded = wasExpandedRef.current;

    // acabou de fechar
    if (wasExpanded && !isExpanded) {
      const vazios = (clientesSel || []).length === 0;

      if (!clientesModoTodos && vazios) {
        setClientesModoTodos(true);
        setClientesSel([]);
      }
    }

    wasExpandedRef.current = isExpanded;
  }, [isExpanded, clientesModoTodos, clientesSel, setClientesModoTodos, setClientesSel]);

  // =========================================================
  // Selecionar / desselecionar todos (somente em modo manual)
  // - Se estava tudo selecionado => passa para vazio (mantém manual)
  // - Se estava vazio/parcial => seleciona todos
  // =========================================================
  const toggleAll = () => {
    if (clientesModoTodos) return;

    const allSelected = (clientesSel || []).length === all.length;

    if (allSelected) {
      setClientesSel([]); // mantém manual, não ativa "Todos incluídos" aqui
      return;
    }

    setClientesSel(all);
  };

  const toggleOne = (val) => {
    if (clientesModoTodos) return;

    setClientesSel((prev) => {
      const arr = Array.isArray(prev) ? prev : [];

      let next;
      if (arr.includes(val)) next = arr.filter((x) => x !== val);
      else next = [...arr, val];

      // NÃO voltar a "Todos incluídos" aqui; isso só acontece ao fechar o accordion
      return next;
    });
  };

  return (
    <Accordion
      expanded={isExpanded}
      onChange={onChange(panelKey)}
      disableGutters
      sx={ACC_SX}
      disabled={disabled}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />} sx={ACC_SUM_SX}>
        <MDTypography variant="button" sx={{ fontSize: 12 }}>
          Clientes
        </MDTypography>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 1.5 }}>
        <MDBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
        >
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={clientesModoTodos}
                onChange={(e) => {
                  const on = e.target.checked;
                  setClientesModoTodos(on);

                  if (on) {
                    // modo "todos" => lista manual limpa
                    setClientesSel([]);
                  } else {
                    // modo manual => começa VAZIO (para poderes escolher 1 rápido)
                    setClientesSel([]);
                  }
                }}
              />
            }
            label={
              <span style={{ fontSize: 12 }}>
                Todos incluídos ({(clientesOptions || []).length})
              </span>
            }
          />

          {!clientesModoTodos && (
            <FormControlLabel
              control={<Checkbox size="small" checked={isAll} onChange={toggleAll} />}
              label={<span style={{ fontSize: 12 }}>Selecionar / desselecionar todos</span>}
            />
          )}
        </MDBox>

        {clientesModoTodos ? (
          <MDBox mt={1}>
            <MDTypography variant="caption" color="text">
              Estão incluídos todos os clientes elegíveis pelos filtros (segmentos, grupos e
              bloqueios).
            </MDTypography>
          </MDBox>
        ) : (
          <>
            <MDBox
              mt={1}
              sx={{
                maxHeight: "45vh",
                overflowY: "auto",
                pr: 1,
                borderRadius: 1,
              }}
            >
              <MDBox display="flex" flexWrap="wrap" gap={1}>
                {(clientesOptions || []).map((o) => (
                  <MDBox key={o.value} sx={{ minWidth: 360 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={(clientesSel || []).includes(o.value)}
                          onChange={() => toggleOne(o.value)}
                        />
                      }
                      label={<span style={{ fontSize: 12 }}>{o.label}</span>}
                    />
                  </MDBox>
                ))}
              </MDBox>
            </MDBox>

            {(clientesSel || []).length === 0 && (
              <MDBox mt={1}>
                <MDTypography variant="caption" color="text">
                  Sem clientes selecionados — ao fechar este accordion, voltará automaticamente a
                  &quot;Todos incluídos&quot;.
                </MDTypography>
              </MDBox>
            )}
          </>
        )}

        {hint && (
          <MDBox mt={1}>
            <MDTypography variant="caption" color="text">
              {hint}
            </MDTypography>
          </MDBox>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
